// Nine Lives '84 - scene camera.
//
// Side view looking down -Z at the XY plane the simulation lives in. Every
// scene is one screen, so the camera has a fixed home: its distance is
// chosen so the whole 320x200 world fits whatever the viewport aspect is,
// with a little breathing room. On top of that it can dolly in toward a
// point (going through a window) and shake (a bite, a shove).

import * as THREE from 'three';
import { WORLD_H, WORLD_W } from '../sim/constants';

const FOV = 32;
const MARGIN = 1.06;
/** How close the dolly gets, as a fraction of the home distance. */
const DOLLY_NEAR = 0.28;

export class SceneCamera {
  readonly camera: THREE.PerspectiveCamera;
  private distance = 300;
  private readonly home = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  /** Dolly target and amount (0 home .. 1 right up to the target). */
  private readonly focus = new THREE.Vector2(WORLD_W / 2, WORLD_H / 2);
  private amount = 0;
  private shakeAmp = 0;
  private readonly tmp = new THREE.Vector3();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(FOV, aspect, 1, 4000);
    this.resize(aspect);
  }

  resize(aspect: number): void {
    const tan = Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    const forHeight = (WORLD_H * MARGIN) / 2 / tan;
    const forWidth = (WORLD_W * MARGIN) / 2 / aspect / tan;
    this.distance = Math.max(forHeight, forWidth);
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.home.set(WORLD_W / 2, WORLD_H / 2 - 4, this.distance);
    this.apply();
  }

  /** Aim the dolly at a world point; `amount` 0 is home, 1 is close in. */
  dolly(x: number, y: number, amount: number): void {
    this.focus.set(x, y);
    this.amount = THREE.MathUtils.clamp(amount, 0, 1);
  }

  /** Kick the camera; the shake decays on its own. */
  shake(amp: number): void {
    this.shakeAmp = Math.max(this.shakeAmp, amp);
  }

  /** Recompute the pose for this frame. */
  update(dt: number): void {
    this.shakeAmp *= Math.exp(-7 * dt);
    if (this.shakeAmp < 0.05) this.shakeAmp = 0;
    this.apply();
  }

  private apply(): void {
    // Ease the dolly so it lingers near home and rushes at the end.
    const t = this.amount * this.amount * (3 - 2 * this.amount);
    const near = this.tmp.set(this.focus.x, this.focus.y, this.distance * DOLLY_NEAR);
    this.camera.position.lerpVectors(this.home, near, t);
    this.look.set(
      THREE.MathUtils.lerp(WORLD_W / 2, this.focus.x, t),
      THREE.MathUtils.lerp(WORLD_H / 2 - 4, this.focus.y, t),
      0,
    );
    if (this.shakeAmp > 0) {
      this.camera.position.x += (Math.random() - 0.5) * this.shakeAmp;
      this.camera.position.y += (Math.random() - 0.5) * this.shakeAmp;
    }
    this.camera.lookAt(this.look);
  }
}
